import { injectable } from 'inversify'
import { action, makeObservable, observable, computed } from 'mobx'
import type { Capability, UserRole, WorkspaceRole } from '@shared/index'

export type WorkspaceMembershipView = {
  workspaceId: string
  name: string
  role: WorkspaceRole
  personal: boolean
}

@injectable()
export class UserModel {
  email: string = null!
  token: string = null!

  /**
   * The email that was just signed out, kept for exactly one session gap so
   * Router can tell "the same person refreshing back in" from "someone else
   * signing in on this browser" when a redirectTarget was stashed after the
   * fact — see Router.redirectTarget's `forEmail`. Cleared (not read) by the
   * next setSession, so it never survives past the login that follows it.
   */
  previousEmail: string = ''

  /**
   * The name a guest chose when entering (POST /guest); null for a real
   * account, which has no such field yet — callers fall back to `email`.
   * Set once at session start, same as `email`/`token`.
   */
  displayName: string | null = null

  /**
   * Display gating only (nav items, route redirects). Authorization is always
   * re-checked server-side per request from the user record — a stale or
   * tampered client role can show a menu item, never grant access.
   */
  role: UserRole | null = null

  /**
   * What the server says this account may do *in the active workspace*, fetched
   * fresh from GET /me (plan slice 6 — capabilities are per-(user, workspace)).
   * Display gating only, same caveat as `role`; the backend re-derives the
   * workspace from the target on every write, never from client state. Switching
   * workspace re-fetches this list (AuthenticationRepository.switchWorkspace).
   */
  capabilities: readonly Capability[] = []

  /**
   * The workspaces this account belongs to (name + workspace role), and which
   * one `capabilities` is currently relative to. Display only — the switcher
   * reads these; nothing here is an authorization decision.
   */
  memberships: readonly WorkspaceMembershipView[] = []
  activeWorkspaceId: string | null = null

  /**
   * Whether this account has dismissed the play-phase "how to move" tip —
   * set from `GET /me` (same as `capabilities`), flipped locally by
   * `setHasSeenPlayTip` once `AuthenticationRepository.dismissPlayTip`'s own
   * POST succeeds. Per-account, not per-browser — survives a device switch.
   */
  hasSeenPlayTip: boolean = false

  constructor() {
    makeObservable(this, {
      email: observable,
      token: observable,
      previousEmail: observable,
      displayName: observable,
      role: observable,
      capabilities: observable.ref,
      memberships: observable.ref,
      activeWorkspaceId: observable,
      hasSeenPlayTip: observable,
      isAuthenticated: computed,
      isAdmin: computed,
      activeWorkspace: computed,
      setSession: action,
      setDisplayName: action,
      setCapabilities: action,
      setWorkspaceContext: action,
      setHasSeenPlayTip: action,
      clearSession: action,
    })
  }

  /**
   * The model owns its own writes. Callers (AuthenticationRepository) set these
   * after awaiting the network, which is outside their own action wrapper —
   * MobX actions do not span an await — so the action has to live here.
   */
  setSession = (email: string, token: string, role: UserRole) => {
    this.email = email
    this.token = token
    this.role = role
    // A fresh session's own login/register/refresh always wins over a stash
    // from whoever was signed in before it.
    this.previousEmail = ''
    this.displayName = null
  }

  /** Guest entry only — see the field's own doc comment. */
  setDisplayName = (displayName: string) => {
    this.displayName = displayName
  }

  /** Set once the identity lands; until then the UI offers nothing gated. */
  setCapabilities = (capabilities: readonly Capability[]) => {
    this.capabilities = capabilities
  }

  /** Set alongside capabilities from GET /me — the two are always consistent. */
  setWorkspaceContext = (memberships: readonly WorkspaceMembershipView[], activeWorkspaceId: string | null) => {
    this.memberships = memberships
    this.activeWorkspaceId = activeWorkspaceId
  }

  /** Set from `GET /me`, and again optimistically once `POST /me/dismiss-play-tip` succeeds. */
  setHasSeenPlayTip = (hasSeenPlayTip: boolean) => {
    this.hasSeenPlayTip = hasSeenPlayTip
  }

  clearSession = () => {
    // Captured before email is blanked below.
    this.previousEmail = this.email
    this.email = ''
    this.token = ''
    this.displayName = null
    this.role = null
    this.capabilities = []
    this.memberships = []
    this.hasSeenPlayTip = false
    this.activeWorkspaceId = null
  }

  /** The membership row `capabilities` is currently relative to, or null. */
  get activeWorkspace(): WorkspaceMembershipView | null {
    return this.memberships.find((membership) => membership.workspaceId === this.activeWorkspaceId) ?? null
  }

  /**
   * Whether to *offer* something. Never a substitute for the server's own
   * check — a tampered list can reveal a menu item, never grant access.
   */
  can = (capability: Capability): boolean => this.capabilities.includes(capability)

  get isAuthenticated(): boolean {
    return !!this.token
  }

  get isAdmin(): boolean {
    return this.role === 'admin'
  }

  /**
   * A passwordless outsider reached only by a job share link. The sharing
   * feature (and the `external` role that came with it) was stripped from
   * this scaffold — kept as a permanent `false` so callers that still branch
   * on it (dead code left over from that strip) keep compiling.
   */
  get isExternal(): boolean {
    return false
  }
}
